import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { productService } from '../../services/productService';
import { Brand } from '../../types';

// Define the initial state interface
interface BrandsState {
  brands: Brand[];
  selectedBrand: Brand | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

// Define the initial state
const initialState: BrandsState = {
  brands: [],
  selectedBrand: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

// Async thunks
export const fetchBrands = createAsyncThunk(
  'brands/fetchBrands',
  async (_, { rejectWithValue }) => {
    try {
      const brands = await productService.getBrands();
      return brands;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch brands');
    }
  }
);

export const fetchBrandById = createAsyncThunk(
  'brands/fetchBrandById',
  async (brandId: string, { rejectWithValue }) => {
    try {
      const brand = await productService.getBrandById(brandId);
      return brand;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch brand');
    }
  }
);

export const createBrand = createAsyncThunk(
  'brands/createBrand',
  async (brandData: Omit<Brand, 'brand_id' | 'create_time' | 'update_time'>, { rejectWithValue, dispatch }) => {
    try {
      const newBrand = await productService.createBrand(brandData);
      // Refresh the brands list
      dispatch(fetchBrands());
      return newBrand;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create brand');
    }
  }
);

export const updateBrand = createAsyncThunk(
  'brands/updateBrand',
  async ({ id, data }: { id: string; data: Partial<Brand> }, { rejectWithValue, dispatch }) => {
    try {
      const updatedBrand = await productService.updateBrand(id, data);
      // Refresh the brands list
      dispatch(fetchBrands());
      return updatedBrand;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update brand');
    }
  }
);

export const deleteBrand = createAsyncThunk(
  'brands/deleteBrand',
  async (brandId: string, { rejectWithValue, dispatch }) => {
    try {
      await productService.deleteBrand(brandId);
      // Refresh the brands list
      dispatch(fetchBrands());
      return brandId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete brand');
    }
  }
);

// Create the brands slice
const brandsSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedBrand: (state, action: PayloadAction<Brand | null>) => {
      state.selectedBrand = action.payload;
    },
    clearSelectedBrand: (state) => {
      state.selectedBrand = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch brands
      .addCase(fetchBrands.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.isLoading = false;
        state.brands = action.payload;
        state.error = null;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch brand by ID
      .addCase(fetchBrandById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrandById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedBrand = action.payload;
        state.error = null;
      })
      .addCase(fetchBrandById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create brand
      .addCase(createBrand.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createBrand.fulfilled, (state) => {
        state.isCreating = false;
        state.error = null;
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update brand
      .addCase(updateBrand.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateBrand.fulfilled, (state) => {
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateBrand.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete brand
      .addCase(deleteBrand.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteBrand.fulfilled, (state) => {
        state.isDeleting = false;
        state.error = null;
      })
      .addCase(deleteBrand.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { clearError, setSelectedBrand, clearSelectedBrand } = brandsSlice.actions;

// Export selectors
export const selectBrands = (state: any) => state.brands.brands;
export const selectSelectedBrand = (state: any) => state.brands.selectedBrand;
export const selectBrandsLoading = (state: any) => state.brands.isLoading;
export const selectBrandsError = (state: any) => state.brands.error;
export const selectBrandsCreating = (state: any) => state.brands.isCreating;
export const selectBrandsUpdating = (state: any) => state.brands.isUpdating;
export const selectBrandsDeleting = (state: any) => state.brands.isDeleting;

// Export reducer
export default brandsSlice.reducer;
